import Clickable from 'components/Common/Clickable'
import FormatCurrency from 'components/Common/FormatCurrency'
import BodyText from 'components/Luxkit/Typography/BodyText'
import Caption from 'components/Luxkit/Typography/Caption'
import { pluralizeToString } from 'lib/string/pluralize'
import moment from 'moment'
import { rem } from 'polished'
import React, { useCallback, useMemo } from 'react'
import styled from 'styled-components'
import cn from 'clsx'
import theme from 'constants/themes/luxuryescapes'
import SolidFireIcon from 'components/Luxkit/Icons/solid/SolidFireIcon'

export const MIN_MONTH_TAB_BUTTON_WIDTH: number = 108

const Tab = styled(Clickable)`
  --stroke-color: ${props => props.theme.palette.neutral.five};
  --stroke-width: 1px;

  color: ${props => props.theme.palette.neutral.two};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: ${rem(92)};
  min-width: ${rem(MIN_MONTH_TAB_BUTTON_WIDTH)};
  background-color: ${props => props.theme.palette.neutral.eight};
  border-radius: ${props => props.theme.borderRadius.S};
  padding: ${rem(8)};
  flex-shrink: 0;
  transition:
    .2s ease background-color,
    .2s ease color,
    .2s ease box-shadow;
  box-shadow: inset 0 0 0 var(--stroke-width) var(--stroke-color);

  &.is-ghost {
    background-color: transparent;
    --stroke-color: transparent;
  }

  &.is-active {
    --stroke-color: ${props => props.theme.palette.neutral.one};
    --stroke-width: 2px;
    color: ${props => props.theme.palette.neutral.one};
  }

  &:not(.is-active):hover {
    --stroke-color: ${props => props.theme.palette.neutral.one};
    color: ${props => props.theme.palette.neutral.one};
  }
`

const DiscountIconContainer = styled.div`
  align-self: flex-end;
  position: absolute;
  top: ${rem(8)};
`

const TitleText = styled(BodyText)`
  & + * {
    margin-top: ${rem(2)};
  }
`

interface Props {
  monthIndex: App.MonthIndex
  startingPrice?: number
  numberOfAvailableDepartures?: number
  numberOfSoldOutDepartures?: number
  isActive?: boolean
  hasDiscount?: boolean
  onMonthSelect?: (monthIndex: App.MonthIndex) => void
}

function MonthTabButton(props: Props) {
  const {
    monthIndex,
    numberOfAvailableDepartures,
    numberOfSoldOutDepartures,
    startingPrice,
    isActive,
    hasDiscount,
    onMonthSelect,
  } = props

  const titleText = useMemo(() => moment().month(monthIndex).format('MMM'), [monthIndex])

  const {
    captionText,
    statusClassName,
  } = useMemo(() => {
    if (numberOfAvailableDepartures) {
      return {
        captionText: `${pluralizeToString('date', numberOfAvailableDepartures)} left`,
      }
    }
    if (numberOfSoldOutDepartures) {
      return {
        captionText: 'SOLD OUT',
      }
    }
    return {
      captionText: 'NO DEPARTURES',
      statusClassName: 'is-ghost',
    }
  }, [numberOfAvailableDepartures, numberOfSoldOutDepartures])

  const handleTabClick = useCallback(() => {
    onMonthSelect?.(monthIndex)
  }, [monthIndex, onMonthSelect])

  return <Tab
    className={cn(statusClassName, { 'is-active': isActive })}
    onClick={handleTabClick}
  >
    {hasDiscount && <DiscountIconContainer>
      <SolidFireIcon color={theme.palette.messaging.urgency.main} size="XS" />
    </DiscountIconContainer>}
    <TitleText variant="large" weight="bold">
      {titleText}
    </TitleText>
    {!!startingPrice && <BodyText
      variant="medium"
      weight="bold"
    >
      <FormatCurrency value={startingPrice} format="roundedDollar" />
    </BodyText>}
    {captionText && <Caption
      variant="medium"
      align="center"
      wrap="word-wrap"
    >
      {captionText}
    </Caption>}
  </Tab>
}

export default MonthTabButton
